import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, distinctUntilChanged, map } from "rxjs";
import { Url } from "../Url";
import { ActivatedRoute, Router } from "@angular/router";
import * as jwt_decode from "jwt-decode";

export const MODES = ["normal", "demo"] as const;
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private activatedRouteService = inject(ActivatedRoute);
  private internalMode$ = new BehaviorSubject<(typeof MODES)[number]>("normal");

  get internalModeValue() {
    return this.internalMode$.value;
  }

  set internalModeValue(mode: (typeof MODES)[number]) {
    if (mode === this.internalMode$.value) return;

    this.internalMode$.next(mode);

    const newTree = this.router.createUrlTree([], {
      queryParams: mode === "demo" ? { mode: "demo" } : { mode: null },
      queryParamsHandling: "merge",
    });
    const newUrl = this.router.serializeUrl(newTree);

    // ? Trick to force reload the route
    // https://stackoverflow.com/questions/40983055/how-to-reload-the-current-route-with-the-angular-2-router?page=1&tab=scoredesc#tab-top
    this.router
      .navigateByUrl("/", {
        skipLocationChange: true,
      })
      .then(() => {
        this.router.navigateByUrl(newUrl);
      });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    // Initialize internal mode from query params
    this.activatedRouteService.queryParamMap
      .pipe(
        map((params) => (params.get("mode") === "demo" ? "demo" : "normal")),
        distinctUntilChanged(),
      )
      .subscribe((mode) => this.internalMode$.next(mode));
  }

  signUp(signUpDto: any): Observable<any> {
    // Define the url to the backend login api
    const url = `${Url}/users/signup`;
    return this.http
      .post<any>(url, {
        first_name: signUpDto.first_name,
        last_name: signUpDto.last_name,
        email: signUpDto.email,
        password: signUpDto.password,
        role: signUpDto.role,
        phone: signUpDto.phone,
        invitation_code: signUpDto.invitationCode,
      })
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  verifySigninCode(signInVerificationCodeDto: any) {
    // Define the url to the backend login api
    const url = `${Url}/users/signin/verify`;
    return this.http
      .post<any>(
        url,
        {
          email: signInVerificationCodeDto.email,
          verification_code: signInVerificationCodeDto.verification_code,
          trust_device: signInVerificationCodeDto.trust_device,
        },
        { withCredentials: true },
      )
      .pipe(
        map((response) => {
          if (response && response.state === "signin" && response.accessToken) {
            localStorage.setItem("jwtToken", response.accessToken);
            //this.resetLogoutTimer(); // Reset the timer on login
          }
          return response;
        }),
      );
  }

  resendCodeSignin(email: string): Observable<any> {
    // Define the url to the backend login api
    const url = `${Url}/users/signin/resend-code`;
    return this.http
      .post<any>(url, {
        email: email,
      })
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  login(email: string, password: string): Observable<any> {
    // Define the url to the backend login api
    const url = `${Url}/users/signin`;
    const normalizedEmail = email.trim().toLowerCase();
    return this.http
      .post<any>(
        url,
        { email: normalizedEmail, password },
        { withCredentials: true },
      )
      .pipe(
        map((response) => {
          if (response && response.state === "signin" && response.accessToken) {
            localStorage.setItem("jwtToken", response.accessToken);
            //this.resetLogoutTimer(); // Reset the timer on login
          }
          return response;
        }),
      );
  }

  forgotPassword(email: string): Observable<any> {
    const url = `${Url}/users/forgot-password`;
    const normalizedEmail = email.trim().toLowerCase();
    return this.http.post<any>(url, { email: normalizedEmail }).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  verifyResetToken(token: string): Observable<any> {
    const url = `${Url}/users/verify-reset-token`;
    return this.http.post<any>(url, { reset_token: token }).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    const url = `${Url}/users/reset-password`;
    return this.http
      .post<any>(url, { reset_token: token, newPassword: newPassword })
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  logout(): void {
    localStorage.removeItem("jwtToken");
    //this.clearLogoutTimer();
    this.router.navigate(["/login"]);
  }

  getToken(): string | null {
    return localStorage.getItem("jwtToken");
  }

  get loggedIn(): boolean {
    return this.getToken() !== null;
  }

  getUserId(): string {
    const decode = this.getDecodedToken();
    return decode["userId"];
  }

  getRole(): string {
    const decode = this.getDecodedToken();
    return decode["role"];
  }

  getEmail(): string | null {
    const decode = this.getDecodedToken();
    return decode?.["email"] ?? null;
  }

  getIsActiveAppliedWater(): boolean {
    const decode = this.getDecodedToken();
    return decode?.["is_active_applied_water"] === true;
  }

  hasRole(role: string): boolean {
    return this.getRole() === role;
  }

  // private clearLogoutTimer() {
  //   if (this.logoutTimer) {
  //     clearTimeout(this.logoutTimer);
  //     this.logoutTimer = null;
  //   }
  // }

  // private startLogoutTimer() {
  //   this.resetLogoutTimer();

  //   document.addEventListener('mousemove', this.resetLogoutTimer.bind(this));
  //   document.addEventListener('keypress', this.resetLogoutTimer.bind(this));
  // }

  // private resetLogoutTimer() {
  //   if (this.logoutTimer) {
  //     clearTimeout(this.logoutTimer);
  //   }

  //   this.logoutTimer = setTimeout(() => {
  //     this.logout();
  //   }, 1800000); // 30 minutes in milliseconds
  // }

  getDecodedToken(): any {
    const token = this.getToken();
    if (token) {
      return jwt_decode.jwtDecode(token);
    }
    return null;
  }
}
